<template>
  <b-form @submit.prevent="submitPerson" :validated="formSubmitted">
    <b-form-group
      class="mb-4 mb-md-5"
      :label="formConfig.booker.label"
      :label-for="formConfig.booker.id"
      label-class="h5"
      v-slot="{ ariaDescribedBy }"
    >
      <b-form-radio-group
        buttons
        button-variant="outline-dark"
        class="d-flex flex-row justify-content-between mt-3"
        :aria-describedby="ariaDescribedBy"
        :id="formConfig.booker.id"
        :name="formConfig.booker.name"
        v-model="formData.booker"
      >
        <b-form-radio
          class="btb-radio-button"
          v-for="{ text, value } in formConfig.booker.options"
          :key="value"
          :value="value"
        >
          {{ text }}
        </b-form-radio>
      </b-form-radio-group>
    </b-form-group>

    <b-form-group
      class="mb-4 mb-md-5"
      :label="formConfig.confirm.label"
      :label-for="formConfig.confirm.id"
      label-class="h5"
      v-slot="{ ariaDescribedBy }"
      v-show="showConfirmPerson"
    >
      <b-form-radio-group
        buttons
        button-variant="outline-dark"
        class="d-flex flex-row justify-content-around mt-3"
        :aria-describedby="ariaDescribedBy"
        :id="formConfig.confirm.id"
        :name="formConfig.confirm.name"
        v-model="formData.confirm"
      >
        <b-form-radio
          class="btb-radio-button"
          v-for="{ text, value } in formConfig.confirm.options"
          :key="value"
          :value="value"
        >
          {{ text }}
        </b-form-radio>
      </b-form-radio-group>
    </b-form-group>

    <b-form-group
      class="mb-4 mb-md-5"
      :label="formConfig.occasion.label"
      :label-for="formConfig.occasion.id"
      label-class="h5"
      :state="occasionState"
      v-slot="{ ariaDescribedBy }"
      v-show="showOccasion"
    >
      <b-form-radio-group
        buttons
        button-variant="outline-dark"
        stacked
        class="d-flex flex-column align-items-start mt-3"
        :aria-describedby="ariaDescribedBy"
        :id="formConfig.occasion.id"
        :name="formConfig.occasion.name"
        :state="occasionState"
        v-model="formData.occasion.id"
      >
        <b-form-radio
          class="btb-radio-button"
          v-for="{ text, value } in formConfig.occasion.options"
          :key="value"
          :value="value"
          :required="formConfig.occasion.required"
        >
          {{ text }}
        </b-form-radio>
      </b-form-radio-group>

      <b-form-group
        class="mt-3"
        :state="occasionState"
        v-if="formData.occasion.id === optionOther.value"
      >
        <span class="btb-form-description">
          Please provide response with minimum 5 characters
        </span>
        <b-textarea
          class="flex-grow-1"
          v-bind="formConfig.occasionOther"
          v-model.trim="formData.occasion.other"
          :required="formConfig.occasionOther && canSubmit"
          :state="occasionState"
        />
      </b-form-group>
    </b-form-group>

    <b-form-group
      v-show="showOccasion"
      :label="formConfig.smsOptin.question"
      :label-for="formConfig.smsOptin.id"
      label-class="h5"
    >
      <b-form-checkbox v-bind="formConfig.smsOptin" v-model="formData.smsOptin">
        <div class="text-muted text-center col-12 col-sm-10 col-md-8 mx-auto pb-4">
          {{ formConfig.smsOptin.label }}
        </div>
      </b-form-checkbox>
    </b-form-group>

    <FindContact
      :showing="showFindContactForm"
      v-show="showFindContactForm"
      v-on="{ [eventsFindContact.COMPLETED]: nextPage }"
    />

    <b-row align-h="center" class="my-3">
      <b-col>
        <b-button variant="outline-dark" @click="goBack"> Back </b-button>
      </b-col>
      <b-col v-show="showSubmit">
        <b-button variant="outline-primary" type="submit"> Next </b-button>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
import { mapGetters, mapState } from "vuex"
import { getDisplayName, isOptionOther } from "@/utils"
import { FETCH_PERSON } from "@/store/actions"
import { GET_TAG_OPTIONS } from "@/store/getters"
import { UPDATE_PERSON } from "@/store/mutations"
import { BOOKER, PERSON, SELECTED_RESERVATION } from "@/store/state"
import FindContact, { FIND_CONTACT_EVENTS } from "./FindContact"
import { UPDATE_OCCASION, UPDATE_SMS_OPTIN } from "@/store/mutations"

const FALLBACK_TAGS_DATA = [
  { text: "Adult Birthday Party", value: 1 },
  { text: "Kids Birthday Party", value: 2 },
  { text: "Bachelor/Bachelorette Party", value: 3 },
  { text: "Date Night", value: 4 },
  { text: "Friends Outing", value: 5 },
  { text: "Family Outing", value: 6 },
  { text: "Corporate Team Building", value: 7 },
  { text: "Kids Field Trip (School, After School, Summer Camp)", value: 9 },
  { text: "Other", value: 18 }
]

export default {
  name: "SomeParentForm",
  components: {
    FindContact
  },
  data() {
    return {
      eventsFindContact: FIND_CONTACT_EVENTS,
      formData: {
        booker: null,
        confirm: null,
        occasion: { id: null, other: null },
        smsOptin: true
      },
      formSubmitted: false
    }
  },
  computed: {
    ...mapState([BOOKER, PERSON, SELECTED_RESERVATION]),
    ...mapGetters([GET_TAG_OPTIONS]),
    canSubmit() {
      const { booker, confirm, occasion } = this.formData
      const { value } = this.optionOther
      if (!booker || !confirm) return false
      if (occasion?.id === value) {
        return !!occasion?.other?.length
      }
      return occasion?.id !== null
    },
    confirmLabel() {
      const displayName = this.getDisplayName(this[BOOKER]?.Person)
      return `Are you ${displayName}?`
    },
    formConfig() {
      return {
        booker: {
          id: "booker-select",
          label: "Are you the person who made the original reservation?",
          name: "booker-select",
          options: [
            { text: "Yes", value: true },
            { text: "No", value: false }
          ]
        },
        confirm: {
          id: "confirm-select",
          label: this.confirmLabel,
          name: "booker",
          options: [
            { text: "Yes", value: true },
            { text: "No", value: false }
          ]
        },
        occasion: {
          id: "occasion-select",
          label: "What is the occasion for your reservation with Beat The Bomb?",
          name: "occasion",
          options: this[GET_TAG_OPTIONS] ?? FALLBACK_TAGS_DATA
        },
        occasionOther: {
          id: "occasion-other-description",
          label: "Please provide response with minimum 5 characters",
          placeholder: "What is the Occasion?",
          rows: "3",
          required: true,
          maxRows: "5"
        },
        smsOptin: {
          id: "contact-sms-optin",
          label:
            "Opt in to get FREE PHOTOS & VIDEOS of your Mission Experience via SMS. By opting in, you agree to receive text messages from Beat The Bomb and third parties acting on our behalf. You can unsubscribe at any time.",
          name: "contact-sms-optin",
          question: "Can we contact you?",
          value: true,
          "unchecked-value": false
        }
      }
    },
    occasionState() {
      return this.formSubmitted ? this.canSubmit : null
    },
    optionOther() {
      const fallbackOptions = this[GET_TAG_OPTIONS] ?? FALLBACK_TAGS_DATA
      return fallbackOptions.find(({ text }) => isOptionOther(text))
    },
    showFindContactForm() {
      return this.formData.booker === false || this.formData.confirm === false
    },
    showConfirmPerson() {
      return this.formData.booker === true
    },
    showOccasion() {
      return this.showConfirmPerson && this.formData.confirm
    },
    showSubmit() {
      return !this.showFindContactForm
    }
  },
  watch: {
    async "formData.booker"(val) {
      await this.updatePerson(val)
    },
    async "formData.confirm"(val) {
      await this.updatePerson(val)
    },
    "formData.occasion": {
      handler(occ) {
        this.updateOccasion(occ)
      },
      deep: true
    }
  },
  methods: {
    getDisplayName,
    goBack() {
      this.$router.back()
    },
    nextPage() {
      this.$router.push({
        name: "PersonDetails",
        query: { personId: this[PERSON].id }
      })
    },
    submitPerson() {
      this.formSubmitted = true
      if (!this.canSubmit) return
      this.$store.commit(UPDATE_SMS_OPTIN, {
        marketing_optin: this.formData.smsOptin
      })
      this.nextPage()
    },
    async updatePerson(isBooker) {
      if (isBooker) {
        // await this.$store.dispatch(
        //   FETCH_PERSON,
        //   this[SELECTED_RESERVATION].Booker.person_id
        // )
        this.$store.commit(UPDATE_PERSON, this[BOOKER]?.Person)
      } else {
        this.$store.commit(UPDATE_PERSON, null)
      }
    },
    updateOccasion(occasion) {
      this.$store.commit(UPDATE_OCCASION, occasion)
    }
  }
}
</script>

<style scoped>
input[type="radio"] {
  visibility: hidden;
}
</style>
